import { PasswordPolicyChecker, passwordPolicyGuard } from '@logto/core-kit';
import {
  AlternativeSignUpIdentifier,
  experience,
  SignInIdentifier,
  SignInMode,
  type SignUpIdentifier,
  type VerificationCodeSignInIdentifier,
} from '@logto/schemas';
import {
  filterSignInConnectors,
  filterSignUpConnectors,
  isSingleSignOnEnabled,
} from '@quickguidehealth/experience-config';
import { condArray } from '@silverhand/essentials';
import { useContext, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import PageContext from '@/Providers/PageContextProvider/PageContext';
// eslint-disable-next-line unused-imports/no-unused-imports -- type only import
import type useRequiredProfileErrorHandler from '@/hooks/use-required-profile-error-handler';
import { useQgExperienceContext } from '@/qg/QgExperienceContext';
import { buildUsernamePolicyDescription } from '@/shared/utils/username-policy-description';
import { type SignInExperienceResponse, type VerificationCodeIdentifier } from '@/types';

type UseSieMethodsReturnType = {
  /**
   * Primary sign-up identifiers, used to render the first screen form of the registration flow.
   *
   * @remarks
   * Currently secondary identifiers are not used when rendering the first screen form.
   * Additional identifiers must be applied in the following user profile fulfillment step.
   * @see {useRequiredProfileErrorHandler} for more information.
   */
  signUpMethods: SignInIdentifier[];
  secondaryIdentifiers: SignUpIdentifier[];
  passwordRequiredForSignUp: boolean;
  signInMethods: SignInExperienceResponse['signIn']['methods'];
  socialSignInSettings: SignInExperienceResponse['socialSignIn'];
  socialConnectors: SignInExperienceResponse['socialConnectors'];
  ssoConnectors: SignInExperienceResponse['ssoConnectors'];
  signInMode: SignInExperienceResponse['signInMode'] | undefined;
  forgotPassword: SignInExperienceResponse['forgotPassword'] | undefined;
  customContent: SignInExperienceResponse['customContent'] | undefined;
  singleSignOnEnabled: boolean | undefined;
  passkeySignIn: SignInExperienceResponse['passkeySignIn'] | undefined;
  /**
   * Check if the given verification code identifier is enabled for sign-up.
   * Used in the verification code sign-in flow, if the verified email/phone number has not been registered,
   * and the identifier type is enabled for sign-up, allow the user to sign-up with the identifier directly.
   */
  isVerificationCodeEnabledForSignUp: (type: VerificationCodeSignInIdentifier) => boolean;
  /**
   * Check if the given verification code identifier is enabled for sign-in.
   * Used in the verification code sign-up flow, if the verified email/phone number has been registered,
   * and the identifier type is enabled for sign-in, allow the user to sign-in with the identifier directly.
   */
  isVerificationCodeEnabledForSignIn: (type: VerificationCodeSignInIdentifier) => boolean;
};

export const useSieMethods = (): UseSieMethodsReturnType => {
  const { experienceSettings } = useContext(PageContext);
  const { resolved: qgProfile } = useQgExperienceContext();
  const { pathname } = useLocation();

  /**
   * Social connectors narrowed by the resolved QuickGuide experience profile.
   * Register-side routes ('register', 'identifier-register') use the sign-up
   * allowlist; everything else uses the sign-in allowlist. When no profile is
   * resolved (config still loading or failed) we pass Logto's list through.
   */
  const socialConnectors = useMemo(() => {
    const all = experienceSettings?.socialConnectors ?? [];
    if (!qgProfile) {
      return all;
    }
    return pathname.includes(experience.routes.register)
      ? filterSignUpConnectors(all, qgProfile)
      : filterSignInConnectors(all, qgProfile);
  }, [experienceSettings?.socialConnectors, qgProfile, pathname]);

  const signUpMethods = useMemo(
    () => experienceSettings?.signUp.identifiers ?? [],
    [experienceSettings]
  );

  const secondaryIdentifiers = useMemo(() => {
    return (
      experienceSettings?.signUp.secondaryIdentifiers?.map(({ identifier }) => identifier) ?? []
    );
  }, [experienceSettings]);

  const isVerificationCodeEnabledForSignUp = useCallback(
    (type: VerificationCodeSignInIdentifier) => {
      if (experienceSettings?.signInMode === SignInMode.SignIn) {
        return false;
      }

      return (
        signUpMethods.includes(type) ||
        secondaryIdentifiers.includes(type) ||
        secondaryIdentifiers.includes(AlternativeSignUpIdentifier.EmailOrPhone)
      );
    },
    [secondaryIdentifiers, signUpMethods, experienceSettings]
  );

  const signInMethods = useMemo(
    () =>
      experienceSettings?.signIn.methods.filter(
        // Filter out empty settings
        ({ password, verificationCode }) => password || verificationCode
      ) ?? [],
    [experienceSettings]
  );

  const isVerificationCodeEnabledForSignIn = useCallback(
    (type: VerificationCodeSignInIdentifier) => {
      if (experienceSettings?.signInMode === SignInMode.Register) {
        return false;
      }

      return Boolean(signInMethods.find(({ identifier }) => identifier === type)?.verificationCode);
    },
    [experienceSettings?.signInMode, signInMethods]
  );

  /**
   * Logto's `singleSignOnEnabled` is tenant-wide, and Turfle shares the tenant
   * with QuickGuide's hospital SSO. The resolved profile narrows it per app, so
   * the "use single sign-on" link and the email-domain SSO detection (both read
   * this flag) stay off where a profile says `signIn.singleSignOn: false`. No
   * profile yet → Logto's value, like the connector lists.
   */
  const singleSignOnEnabled = useMemo(
    () =>
      Boolean(experienceSettings?.singleSignOnEnabled) &&
      (!qgProfile || isSingleSignOnEnabled(qgProfile)),
    [experienceSettings?.singleSignOnEnabled, qgProfile]
  );

  const passwordRequiredForSignUp = useMemo(() => {
    const { signUp } = experienceSettings ?? {};
    return signUp?.password ?? false;
  }, [experienceSettings]);

  return useMemo(
    () => ({
      signUpMethods,
      signInMethods,
      secondaryIdentifiers,
      socialSignInSettings: experienceSettings?.socialSignIn ?? {},
      socialConnectors,
      ssoConnectors: experienceSettings?.ssoConnectors ?? [],
      signInMode: experienceSettings?.signInMode,
      forgotPassword: experienceSettings?.forgotPassword,
      customContent: experienceSettings?.customContent,
      singleSignOnEnabled,
      passkeySignIn: experienceSettings?.passkeySignIn,
      passwordRequiredForSignUp,
      isVerificationCodeEnabledForSignUp,
      isVerificationCodeEnabledForSignIn,
    }),
    [
      signUpMethods,
      signInMethods,
      secondaryIdentifiers,
      experienceSettings?.socialSignIn,
      socialConnectors,
      experienceSettings?.ssoConnectors,
      experienceSettings?.signInMode,
      experienceSettings?.forgotPassword,
      experienceSettings?.customContent,
      singleSignOnEnabled,
      experienceSettings?.passkeySignIn,
      passwordRequiredForSignUp,
      isVerificationCodeEnabledForSignUp,
      isVerificationCodeEnabledForSignIn,
    ]
  );
};

export const usePasswordPolicy = () => {
  const { t } = useTranslation();
  const { experienceSettings } = useContext(PageContext);
  const policy = useMemo(
    () => passwordPolicyGuard.parse(experienceSettings?.passwordPolicy ?? {}),
    [experienceSettings]
  );
  const policyChecker = useMemo(() => new PasswordPolicyChecker(policy), [policy]);

  const requirementsDescription = useMemo(
    () =>
      policy.length.min <= 1 && policy.characterTypes.min <= 1
        ? undefined
        : t('description.password_requirements', {
            items: condArray(
              // There's no need to show the length requirement if it can be satisfied by the character types
              policy.length.min > policy.characterTypes.min &&
                t('description.password_requirement.length', { count: policy.length.min }),
              // Show the character types requirement if it's greater than 1.
              policy.characterTypes.min > 1 &&
                t('description.password_requirement.character_types', {
                  count: policy.characterTypes.min,
                })
            ),
          }),
    [t, policy.length.min, policy.characterTypes.min]
  );

  return {
    policy,
    policyChecker,
    /** The localized description of the password policy. */
    requirementsDescription,
  };
};

/**
 * The localized username policy requirements hint, or `undefined` when the policy is the permissive
 * default (nothing to surface). Mirrors {@link usePasswordPolicy}'s `requirementsDescription`.
 */
export const useUsernamePolicyDescription = () => {
  const { t } = useTranslation();
  const { experienceSettings } = useContext(PageContext);

  return useMemo(
    () => buildUsernamePolicyDescription(experienceSettings?.usernamePolicy, t),
    [experienceSettings?.usernamePolicy, t]
  );
};

export const useForgotPasswordSettings = () => {
  const { experienceSettings } = useContext(PageContext);
  const { forgotPassword } = experienceSettings ?? {};

  const enabledMethodSet = new Set<VerificationCodeIdentifier>();

  if (forgotPassword?.email) {
    enabledMethodSet.add(SignInIdentifier.Email);
  }

  if (forgotPassword?.phone) {
    enabledMethodSet.add(SignInIdentifier.Phone);
  }

  return {
    isForgotPasswordEnabled: enabledMethodSet.size > 0,
    enabledMethodSet,
  };
};
